/**
 * AppSwitcher — the nine-dot app launcher in the top bar's right corner.
 *
 * Lists the apps the signed-in role can open, each as a row with a round, brand-
 * tinted icon, its name and a one-line description. It starts out thin (most roles
 * have one or two apps today) and grows as the apps split out. Gating
 * mirrors the sidebar (DrawerComponents.js), so an app shows here only if it shows there,
 * in the same order and with the same icon. CareMap and CareMap2 are full-screen
 * apps outside the dashboard, so they live here and not in the sidebar.
 */

import React, { useState } from "react";
import { masterChartPath } from "constants/masterChartRoutes";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RecordVoiceOverRounded from "@mui/icons-material/RecordVoiceOverRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import AssignmentRounded from "@mui/icons-material/AssignmentRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import NoteAltRounded from "@mui/icons-material/NoteAltRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import FactCheckRounded from "@mui/icons-material/FactCheckRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import VerifiedRounded from "@mui/icons-material/VerifiedRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import MapRounded from "@mui/icons-material/MapRounded";
import LocationCityRounded from "@mui/icons-material/LocationCityRounded";
import CampaignRounded from "@mui/icons-material/CampaignRounded";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import { useAuth } from "hooks/useAuth";
import { isDevEnvironment } from "utils/isDevEnvironment";

// Google's launcher dots: nine circles. @mui/icons-material only has square-dot grids
// (Apps, AppsRounded), so this one is drawn with SvgIcon.
const AppsDotsIcon = (props) => (
  <SvgIcon {...props}>
    {[5, 12, 19].flatMap((cy) =>
      [5, 12, 19].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2.2} />),
    )}
  </SvgIcon>
);

const APPS = {
  caremap: {
    // Its own section on the role Home pages: it is added after every role's apps,
    // and without a group it would trail the last section with no heading of its own.
    group: "Find care",
    name: "CareMap",
    description: "Find care near you",
    icon: <MapRounded />,
    path: "/caremap",
  },
  caremap2: {
    group: "Find care",
    name: "CareMap2",
    description: "Cities, medical schools, and local care",
    icon: <LocationCityRounded />,
    path: "/caremap2",
  },
  // FusionMSI catalog: btybMarketer gets this tile only.
  btyb: {
    name: "BTYB",
    description: "Run your BTYB sponsor auction",
    icon: <StorefrontRounded />,
    path: "/dashboard/btyb",
  },
  // FusionMSI catalog: the carousel / zip-slot desk (CareMap Ads).
  // On FusionMSI that tile belongs to marketingExecutive. Sandbox assigns it
  // to the Fusion Affiliate advertiser, who buys carousel ads and no clinical apps.
  caremapAds: {
    name: "CareMap Ads",
    description: "Manage your CareMap zip-slot ads",
    icon: <CampaignRounded />,
    path: "/dashboard/advertising/slots",
  },
  carestart: {
    name: "CareStart",
    description: "Talk through your symptoms",
    icon: <RecordVoiceOverRounded />,
    path: "/dashboard/carestart",
  },
  carestart3: {
    name: "CareStart 3",
    description: "Symptoms in your language",
    icon: <TranslateRounded />,
    path: "/dashboard/carestart3",
  },
  medicalRecord: {
    name: "MasterChart",
    description: "Your health history in one place",
    icon: <HealthAndSafetyRounded />,
    path: masterChartPath(),
  },
  myMedications: {
    name: "My Medications",
    description: "Keep your medication list current",
    icon: <MedicationRounded />,
    path: "/dashboard/medications",
  },
  dischargeInstructions: {
    name: "Discharge Instructions",
    description: "Summaries from your facility visits",
    icon: <AssignmentRounded />,
    path: "/dashboard/discharge-instructions",
  },
  shareWithProvider: {
    name: "Share with provider",
    description: "Let a clinician see your record",
    icon: <QrCode2Rounded />,
    path: masterChartPath("share"),
  },
  chartmind: {
    group: "Clinical",
    name: "ChartMind",
    description: "AI notes for every visit",
    icon: <NoteAltRounded />,
    path: "/dashboard/chartmind",
  },
  peerview: {
    group: "Clinical",
    name: "PeerView",
    description: "Discuss cases with colleagues",
    icon: <ForumRounded />,
    path: "/dashboard/peerview",
  },
  myCharts: {
    group: "Clinical",
    name: "My Charts",
    description: "View saved encounters",
    icon: <HistoryRounded />,
    path: "/dashboard/chartmind/sessions",
  },
  morningReport: {
    group: "Education",
    name: "MorningReport",
    description: "Daily cases to learn from",
    icon: <SchoolRounded />,
    path: "/dashboard/morning-report",
  },
  visitstart: {
    name: "VisitStart",
    description: "Patient check-in for your facility",
    icon: <FactCheckRounded />,
    path: "/dashboard/visitstart",
  },
  prompts: {
    group: "Content",
    name: "QueryManager",
    description: "Manage the AI prompts",
    icon: <PsychologyRounded />,
    path: "/dashboard/prompts",
  },
  morningReportCases: {
    group: "Content",
    name: "MorningReport Cases",
    description: "Write and publish cases",
    icon: <LocalHospitalRounded />,
    path: "/dashboard/admin/morning-report-cases",
  },
  systemHealth: {
    group: "Monitoring",
    name: "System Health",
    description: "Uptime and health checks",
    icon: <MonitorHeartRounded />,
    path: "/dashboard/admin/health",
  },
  locationApproval: {
    group: "People",
    name: "Location Approval",
    description: "Approve new facility locations",
    icon: <VerifiedRounded />,
    path: "/dashboard/admin/approval",
  },
  regionalAdmins: {
    group: "People",
    name: "Regional admins",
    description: "Manage regional prompt editors",
    icon: <PeopleRounded />,
    path: "/dashboard/admin/regional-admins",
  },
  usageMonitor: {
    group: "Monitoring",
    name: "Usage Monitor",
    description: "AI usage and costs",
    icon: <BarChartRounded />,
    path: "/dashboard/admin/usage",
  },
  hipaaCompliance: {
    group: "Monitoring",
    name: "HIPAA Compliance",
    description: "Compliance tasks and evidence",
    icon: <FactCheckRounded />,
    path: "/dashboard/admin/hipaa-compliance",
  },
  // A ChartMind manager's org tools.
  team: {
    name: "Team",
    description: "Manage your team",
    icon: <BusinessRounded />,
    path: "/dashboard/team",
  },
  orgUsage: {
    name: "Usage",
    description: "Encounters and team activity",
    icon: <BarChartRounded />,
    path: "/dashboard/usage",
  },
};

/**
 * Apps split into the sidebar's sections (an app's `group`), in first-seen order.
 * Roles whose apps have no group come back as one untitled section.
 */
export const groupApps = (apps) =>
  apps.reduce((sections, app) => {
    const title = app.group || null;
    const section = sections.find((s) => s.title === title);
    if (section) section.apps.push(app);
    else sections.push({ title, apps: [app] });
    return sections;
  }, []);

/** The apps the signed-in role can open. Shared with the role Home page (HomePage.jsx). */
export const useRoleApps = () => {
  const apps = useSidebarApps();
  const { userData, isGlobalAdmin, isRegionalAdmin } = useAuth();
  // CareMap is for every role but MorningReport learners and the two advertising
  // roles, and only on dev servers until it is ready for production (its route
  // is gated the same way in AppRoutes.jsx). CareMap2 is for every role on those
  // same servers, including learners and both advertising roles.
  const role = userData?.role;
  const isLearner = !isGlobalAdmin && !isRegionalAdmin && role === "learner";
  const advertisingOnly = role === "btybMarketer" || role === "fusionAffiliate";
  if (!isDevEnvironment()) return apps;
  const withCareMap =
    apps.length === 0 || isLearner || advertisingOnly ? apps : [...apps, APPS.caremap];
  const knownRole =
    isGlobalAdmin ||
    isRegionalAdmin ||
    role === "patient" ||
    role === "professional" ||
    role === "learner" ||
    role === "btybMarketer" ||
    role === "fusionAffiliate" ||
    role === "facility" ||
    role === "admin";
  if (!knownRole) return withCareMap;
  return [...withCareMap, APPS.caremap2];
};

/** The role's apps that also have a sidebar entry. */
const useSidebarApps = () => {
  const { userData, isGlobalAdmin, isRegionalAdmin } = useAuth();

  if (isGlobalAdmin) {
    return [
      APPS.prompts,
      APPS.morningReportCases,
      APPS.locationApproval,
      APPS.regionalAdmins,
      APPS.usageMonitor,
      APPS.systemHealth,
      APPS.hipaaCompliance,
    ];
  }
  if (isRegionalAdmin) return [APPS.prompts];

  switch (userData?.role) {
    case "patient":
      return [
        APPS.carestart,
        APPS.carestart3,
        APPS.medicalRecord,
        APPS.myMedications,
        APPS.dischargeInstructions,
        APPS.shareWithProvider,
      ];
    case "professional":
      return [APPS.chartmind, APPS.peerview, APPS.myCharts];
    case "learner":
      return [APPS.morningReport];
    case "btybMarketer":
      return [APPS.btyb];
    case "fusionAffiliate":
      return [APPS.caremapAds];
    case "facility":
      return [APPS.visitstart];
    case "admin":
      return [APPS.prompts, APPS.team, APPS.orgUsage];
    default:
      return [];
  }
};

/**
 * @param {Function} [onNavigate] called before navigating, e.g. to close a mobile drawer.
 * @param {boolean} [opensDown] open the list below the button, right-aligned, for a launcher
 *   in a top-right corner as Google has it (CareMap) rather than at the foot of the sidebar.
 */
const AppSwitcher = ({ onNavigate, opensDown = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const apps = useRoleApps();

  if (apps.length === 0) return null;

  const open = (path) => {
    setAnchorEl(null);
    onNavigate?.();
    navigate(path);
  };

  return (
    <>
      <Tooltip title="Apps">
        <IconButton
          aria-label="Apps"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <AppsDotsIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        // Opens downward from a top corner; otherwise (CareMap's sidebar foot) upward.
        anchorOrigin={
          opensDown
            ? { vertical: "bottom", horizontal: "right" }
            : { vertical: "top", horizontal: "left" }
        }
        transformOrigin={
          opensDown
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "left" }
        }
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "min(600px, calc(100dvh - 96px))",
              borderRadius: 3,
              ...(opensDown ? { mt: 1 } : { mb: 1 }),
            },
          },
        }}
      >
        {/* A plain list in the sidebar's order, each app with its one-line
            description. No section headings: with a handful of apps they cost more
            than they explain. The brand shows in the icons: each sits in a soft
            primary-tinted circle, and the app you are in gets a solid one. */}
        <List component="nav" aria-label="Apps" sx={{ p: 1 }}>
          {apps.map((app) => {
            // Longest matching path wins so /chartmind/sessions lights My Charts,
            // not ChartMind.
            const current =
              apps
                .filter(
                  (candidate) =>
                    pathname === candidate.path ||
                    pathname.startsWith(`${candidate.path}/`),
                )
                .sort((a, b) => b.path.length - a.path.length)[0]?.path ===
              app.path;
            return (
              <ListItemButton
                key={app.path}
                selected={current}
                onClick={() => open(app.path)}
                aria-current={current ? "page" : undefined}
                sx={{ borderRadius: 2, px: 1.5, my: "2px" }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={({ palette }) => ({
                      width: 40,
                      height: 40,
                      bgcolor: current
                        ? palette.primary.main
                        : alpha(palette.primary.main, 0.12),
                      color: current ? palette.primary.contrastText : palette.primary.main,
                    })}
                  >
                    {app.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={app.name}
                  secondary={app.description}
                  primaryTypographyProps={{ variant: "subtitle2" }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default AppSwitcher;
